import { Car } from "@views/Upholstery/types"
import {
  applyUpholsteryVehicleToStores,
  getUpholsteryVehicleList,
  isPdmStructureV2Enabled,
  loadUpholsteryVehicleSelection,
  logVehicleSelectionError,
  usePdmVehicleCatalogStore,
} from "@views/UpholsteryV2"

/**
 * Helper function to convert string to slug format
 */
const toSlug = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
}

/**
 * Helper function to find upholstery car by make/model/year slugs
 */
function findUpholsteryCarBySlugs(
  upholsteryCars: Car[],
  makeSlug: string,
  modelSlug: string,
  year: string
): Car | undefined {
  return upholsteryCars.find((car) => {
    const carMakeSlug = toSlug(car.make)
    const carModelSlug = toSlug(car.model)

    return (
      carMakeSlug === makeSlug.toLowerCase() &&
      carModelSlug === modelSlug.toLowerCase() &&
      car.years.split(",").some((y) => y.trim() === year)
    )
  })
}

/**
 * Hook to save vehicle selection to the upholstery store
 * Fetches full vehicle data from API and saves it the same way as the upholstery page
 */
export function useSaveVehicleToStore() {
  const saveVehicle = async (
    makeSlug: string,
    modelSlug: string,
    year: string
  ): Promise<boolean> => {
    if (isPdmStructureV2Enabled()) {
      const { loading, pickerCars } = usePdmVehicleCatalogStore.getState()
      if (loading || pickerCars.length === 0) {
        return false
      }
    }

    const upholsteryCars = getUpholsteryVehicleList()

    if (!makeSlug || !modelSlug || !year || upholsteryCars.length === 0) {
      return false
    }

    const upholsteryCar = findUpholsteryCarBySlugs(
      upholsteryCars,
      makeSlug,
      modelSlug,
      year
    )

    if (!upholsteryCar) {
      return false
    }

    try {
      const { datoVehicle, vehicleData } = await loadUpholsteryVehicleSelection(
        upholsteryCar.make,
        upholsteryCar.model,
        year
      )

      applyUpholsteryVehicleToStores(vehicleData, datoVehicle)
      return true
    } catch (error) {
      logVehicleSelectionError(error, {
        make: upholsteryCar.make,
        model: upholsteryCar.model,
        year,
      })
      return false
    }
  }

  return { saveVehicle }
}
